import React from 'react';

// Format an ISO date/string as e.g. "July 8, 2026". Returns '' for falsy input.
export const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

// Rough read-time estimate (~200 wpm) from a post's text content. Min 1 min.
export const readTime = (post) => {
  const parts = [post?.excerpt || ''];
  (post?.blocks || []).forEach((b) => {
    if (b.text) parts.push(b.text);
    if (Array.isArray(b.items)) parts.push(b.items.join(' '));
    if (b.caption) parts.push(b.caption);
  });
  const words = parts.join(' ').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

// Derive a short teaser when a post has no explicit excerpt: first paragraph.
export const excerptFor = (post) => {
  if (post?.excerpt) return post.excerpt;
  const para = (post?.blocks || []).find((b) => b.type === 'paragraph' && b.text);
  return para ? para.text.replace(/[*[\]()]/g, '') : '';
};

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
const SIZE_EM = { '-2': 0.75, '-1': 0.875, 0: 1, 1: 1.15, 2: 1.3 };

const sanitizeInlineUrl = (raw) => {
  const url = String(raw || '').trim();
  if (/^(https?:|mailto:)/i.test(url)) return url;
  if (/^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(url)) return `https://${url}`;
  return '';
};

const parseSpanAttrs = (raw) => {
  const attrs = {};
  raw.split(',').forEach((pair) => {
    const [k, v] = pair.split(':');
    if (k === 'sz' && v != null) {
      const n = Math.max(-2, Math.min(2, Math.round(Number(v) || 0)));
      if (n !== 0) attrs.size = n;
    }
    if (k === 'c' && HEX_COLOR.test(v || '')) attrs.color = v.toLowerCase();
  });
  return attrs;
};

// Spans are the one mark whose open and close tokens differ ({{…}} vs {{/}}),
// so they can nest (colour + size on the same run). Find the {{/}} that closes
// the span opening at `from`, stepping over nested spans, rather than the first
// {{/}} — otherwise the outer span steals the inner one's terminator and the
// reader sees raw token text. Mirrors findSpanClose in the admin parser.
const SPAN_OPEN = /\{\{[^}]*\}\}/y;
const findSpanClose = (str, from) => {
  let depth = 0;
  let i = from;
  while (i < str.length) {
    if (str.startsWith('{{/}}', i)) {
      if (depth === 0) return i;
      depth -= 1;
      i += 5;
      continue;
    }
    SPAN_OPEN.lastIndex = i;
    const open = SPAN_OPEN.exec(str);
    if (open) { depth += 1; i += open[0].length; continue; }
    i += 1;
  }
  return -1;
};

// Same grammar as adminPanel/src/components/admin/blog/richtext/markdown.js's
// parseInline — duplicated because admin and public are separate bundles.
// Keep the two in sync when the dialect changes.
const parseInlineNodes = (md) => {
  const str = String(md || '');
  const nodes = [];
  let buf = '';
  let i = 0;
  const flush = () => { if (buf) { nodes.push({ type: 'text', value: buf }); buf = ''; } };
  const wrap = (type, extra, openLen, closeIdx, closeLen) => {
    flush();
    nodes.push({ type, ...extra, children: parseInlineNodes(str.slice(i + openLen, closeIdx)) });
    i = closeIdx + closeLen;
  };
  while (i < str.length) {
    const rest = str.slice(i);
    const link = /^\[([^\]]+)\]\(([^)]+)\)/.exec(rest);
    if (link) {
      flush();
      nodes.push({ type: 'link', href: link[2], children: [{ type: 'text', value: link[1] }] });
      i += link[0].length;
      continue;
    }
    const span = /^\{\{([^}]*)\}\}/.exec(rest);
    if (span) {
      const closeIdx = findSpanClose(str, i + span[0].length);
      if (closeIdx !== -1) { wrap('span', parseSpanAttrs(span[1]), span[0].length, closeIdx, 5); continue; }
    }
    if (rest.startsWith('**')) {
      const closeIdx = str.indexOf('**', i + 2);
      if (closeIdx > i + 1) { wrap('bold', {}, 2, closeIdx, 2); continue; }
    }
    if (rest.startsWith('__')) {
      const closeIdx = str.indexOf('__', i + 2);
      if (closeIdx > i + 1) { wrap('underline', {}, 2, closeIdx, 2); continue; }
    }
    if (rest.startsWith('~~')) {
      const closeIdx = str.indexOf('~~', i + 2);
      if (closeIdx > i + 1) { wrap('strike', {}, 2, closeIdx, 2); continue; }
    }
    if (rest[0] === '*') {
      const closeIdx = str.indexOf('*', i + 1);
      if (closeIdx > i) { wrap('italic', {}, 1, closeIdx, 1); continue; }
    }
    buf += str[i];
    i += 1;
  }
  flush();
  return nodes;
};

// Render a small subset of Markdown inline as React nodes — no
// dangerouslySetInnerHTML, so it's XSS-safe.
export const renderInline = (text) => {
  if (!text) return null;
  let key = 0;
  const toReact = (node) => {
    if (node.type === 'text') return node.value;
    const children = node.children.map(toReact);
    const k = key++;
    switch (node.type) {
      case 'bold': return <strong key={k} style={{ color: '#f1f5f9', fontWeight: 700 }}>{children}</strong>;
      case 'italic': return <em key={k}>{children}</em>;
      case 'underline': return <u key={k}>{children}</u>;
      case 'strike': return <s key={k}>{children}</s>;
      case 'link': {
        const href = sanitizeInlineUrl(node.href);
        return href
          ? <a key={k} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', textDecoration: 'none', borderBottom: '1px solid rgba(96,165,250,0.35)' }}>{children}</a>
          : children;
      }
      case 'span': {
        const style = {};
        if (node.size) style.fontSize = `${SIZE_EM[node.size]}em`;
        if (node.color) style.color = node.color;
        return Object.keys(style).length ? <span key={k} style={style}>{children}</span> : children;
      }
      default: return children;
    }
  };
  return parseInlineNodes(text).map(toReact);
};

// A tasteful gradient stand-in when a post/cover has no image, so cards and
// heroes never show a broken image.
export const coverFallback = (seed = '') => {
  const gradients = [
    'linear-gradient(135deg, #1e3a8a, #3b82f6 70%, #60a5fa)',
    'linear-gradient(135deg, #1e293b, #4338ca 75%, #6366f1)',
    'linear-gradient(135deg, #0f766e, #14b8a6 70%, #5eead4)',
    'linear-gradient(135deg, #4c1d95, #7c3aed 70%, #a78bfa)',
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash + seed.charCodeAt(i)) % gradients.length;
  return gradients[hash];
};

const LEGACY_IMAGE_WIDTH = {
  full:   { widthPercent: 100, align: 'full' },
  wide:   { widthPercent: 100, align: 'full' },
  center: { widthPercent: 66,  align: 'center' },
  left:   { widthPercent: 50,  align: 'left' },
  right:  { widthPercent: 50,  align: 'right' },
};

// Style for an image block's wrapper. Prefers the v2 widthPercent/align
// fields; falls back to the legacy v1 width enum.
export const imageLayout = (block = {}) => {
  const legacy = LEGACY_IMAGE_WIDTH[block.width] || LEGACY_IMAGE_WIDTH.full;
  let pct = block.widthPercent == null ? legacy.widthPercent : block.widthPercent;
  let align = block.align == null ? legacy.align : block.align;
  pct = Math.max(10, Math.min(100, Math.round(Number(pct) || 100)));
  if (!['left', 'right', 'center', 'full'].includes(align)) align = 'full';
  if (align === 'full') return { width: '100%' };
  const style = { width: `${pct}%` };
  if (align === 'center') style.margin = '0 auto';
  if (align === 'right') style.marginLeft = 'auto';
  return style;
};
